import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Trash, Factory, Wand2, FileText } from "lucide-react";
import { api, API, fmtINR, fmtDate, apiError } from "../lib/api";
import { UNITS } from "../lib/modules";
import { useAuth } from "../context/AuthContext";
import { canWrite, canDelete } from "../lib/permissions";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const n = (v) => Number(v || 0);
const qtyf = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
const emptyMat = () => ({ product_id: "", product_name: "", quantity: "", unit: "MT" });

const MatTable = ({ title, rows, setRows, setter, opts, testid, rateMap }) => (
  <div className="mt-3">
    <div className="flex items-center justify-between mb-2">
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</label>
      <Button data-testid={`add-${testid}-btn`} size="sm" variant="outline" className="rounded-sm gap-1" onClick={() => setRows([...rows, emptyMat()])}><Plus className="w-3.5 h-3.5" /> Add</Button>
    </div>
    <div className="border border-slate-200 rounded-sm overflow-x-auto">
      <table className="w-full text-xs">
        <thead><tr className="bg-slate-50 text-left text-slate-500"><th className="px-2 py-2 min-w-[200px]">Material</th><th className="px-2 py-2 w-28">Quantity</th><th className="px-2 py-2 w-24">Unit</th><th className="px-2 py-2 w-36 text-right">Actual Rate</th><th className="px-2 py-2 w-28 text-right">Total Cost</th><th className="px-2 py-2 w-8"></th></tr></thead>
        <tbody>
          {rows.map((l, i) => {
            const r = rateMap[l.product_id] || {};
            const rate = l.rate_snapshot != null && Number(l.rate_snapshot) > 0 ? Number(l.rate_snapshot) : Number(r.rate || 0);
            const src = l.rate_snapshot != null && Number(l.rate_snapshot) > 0 ? (l.rate_source || "inventory_actual_unit_cost") : r.rate_source;
            return (
            <tr key={i} className="border-t border-slate-100">
              <td className="px-2 py-1.5">
                <Select value={l.product_id || ""} onValueChange={(v) => setter(i, "product_id", v)}>
                  <SelectTrigger data-testid={`${testid}-${i}-product`} className="bg-white h-8"><SelectValue placeholder="Select material" /></SelectTrigger>
                  <SelectContent>{opts.map((p) => <SelectItem key={p.id} value={p.id}>{p.product_name}</SelectItem>)}</SelectContent>
                </Select>
              </td>
              <td className="px-2 py-1.5"><Input data-testid={`${testid}-${i}-qty`} type="number" step="0.001" value={l.quantity} onChange={(e) => setter(i, "quantity", e.target.value)} className="h-8 bg-white" /></td>
              <td className="px-2 py-1.5"><Select value={l.unit || "MT"} onValueChange={(v) => setter(i, "unit", v)}><SelectTrigger className="bg-white h-8"><SelectValue /></SelectTrigger><SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select></td>
              <td className="px-2 py-1.5 text-right" data-testid={`${testid}-${i}-rate`}>
                <div className="font-mono tabular-nums text-slate-900">{l.product_id ? `${fmtINR(rate)}${l.unit ? ` / ${l.unit}` : ""}` : "—"}</div>
                {l.product_id && <div className={`text-[10px] ${src === "inventory_actual_unit_cost" ? "text-emerald-700" : src === "reference_rate" ? "text-amber-700" : "text-red-600"}`} data-testid={`${testid}-${i}-rate-source`}>
                  {src === "inventory_actual_unit_cost" ? "Rate Source: Inventory Actual Unit Cost" : src === "reference_rate" ? "Rate Source: Reference Rate" : "No cost on record"}
                </div>}
              </td>
              <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-600" data-testid={`${testid}-${i}-total`}>{fmtINR(Number(l.quantity || 0) * rate)}</td>
              <td className="px-2 py-1.5">{rows.length > 1 && <button onClick={() => setRows(rows.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-600"><Trash className="w-3.5 h-3.5" /></button>}</td>
            </tr>);
          })}
        </tbody>
      </table>
    </div>
  </div>
);

export default function Production() {
  const { user } = useAuth();
  const canW = canWrite(user, "production");
  const canDel = canDelete(user, "production");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [raw, setRaw] = useState([emptyMat()]);
  const [pack, setPack] = useState([emptyMat()]);
  const [products, setProducts] = useState([]);
  const [rateMap, setRateMap] = useState({});
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setItems((await api.get("/production")).data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => {
    load();
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/products/cost-rates").then((r) => setRateMap(r.data.rates || {})).catch(() => {});
  }, []);

  const activeProducts = useMemo(() => products.filter((p) => String(p.status || "Active").toLowerCase() !== "inactive"), [products]);
  const byType = (t) => activeProducts.filter((p) => p.product_type === t);
  const finishedOpts = byType("Finished Goods").length ? byType("Finished Goods") : activeProducts;
  const rawOpts = byType("Raw Material").length ? byType("Raw Material") : activeProducts;
  const packOpts = byType("Packaging Material").length ? byType("Packaging Material") : activeProducts;

  const openCreate = () => {
    setEditing(null);
    setForm({ status: "Completed", production_date: new Date().toISOString().slice(0, 10), unit: "MT", labour_cost: 0, electricity_cost: 0, machine_cost: 0, other_cost: 0 });
    setRaw([emptyMat()]); setPack([emptyMat()]);
    setOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({ ...row });
    setRaw(row.raw_materials?.length ? row.raw_materials.map((m) => ({ ...m })) : [emptyMat()]);
    setPack(row.packaging?.length ? row.packaging.map((m) => ({ ...m })) : [emptyMat()]);
    setOpen(true);
  };

  const setMat = (setList, opts) => (i, key, val) => {
    setList((prev) => {
      const copy = prev.map((l) => ({ ...l }));
      copy[i][key] = val;
      if (key === "product_id") {
        const p = opts.find((x) => x.id === val);
        if (p) { copy[i].product_name = p.product_name; copy[i].unit = p.unit || "MT"; }
      }
      return copy;
    });
  };
  const setRawMat = setMat(setRaw, rawOpts);
  const setPackMat = setMat(setPack, packOpts);

  const loadBom = () => {
    const fp = finishedOpts.find((p) => p.id === form.product_id);
    if (!fp) { toast.error("Select a finished product first"); return; }
    const q = n(form.production_quantity);
    if (!(q > 0)) { toast.error("Enter output quantity first"); return; }
    const scale = (arr) => (arr || []).map((b) => ({ product_id: b.product_id, product_name: b.product_name, quantity: n(b.quantity) * q, unit: b.unit }));    const r = scale(fp.bom_raw), p = scale(fp.bom_packaging);
    if (r.length === 0 && p.length === 0) { toast.error("No BOM defined on this product. Define it in the Products page."); return; }
    setRaw(r.length ? r : [emptyMat()]);
    setPack(p.length ? p : [emptyMat()]);
    toast.success("Recipe loaded and scaled to output quantity");
  };

  const lineRate = (l) => (l.rate_snapshot != null && n(l.rate_snapshot) > 0 ? n(l.rate_snapshot) : n((rateMap[l.product_id] || {}).rate));

  const costs = useMemo(() => {
    const rawCost = raw.reduce((s, l) => s + n(l.quantity) * lineRate(l), 0);
    const pkgCost = pack.reduce((s, l) => s + n(l.quantity) * lineRate(l), 0);
    const total = rawCost + pkgCost + n(form.labour_cost) + n(form.electricity_cost) + n(form.machine_cost) + n(form.other_cost);
    const perUnit = n(form.production_quantity) > 0 ? total / n(form.production_quantity) : 0;
    return { rawCost, pkgCost, total, perUnit };
  }, [raw, pack, form.labour_cost, form.electricity_cost, form.machine_cost, form.other_cost, form.production_quantity, rateMap]);

  const save = async () => {
    if (!form.batch_number) { toast.error("Batch Number is required"); return; }
    if (!form.product_id) { toast.error("Finished product is required"); return; }
    if (!(n(form.production_quantity) > 0)) { toast.error("Output quantity must be greater than 0"); return; }
    const clean = (l) => l.filter((m) => m.product_id && n(m.quantity) > 0).map((m) => ({ product_id: m.product_id, product_name: m.product_name, quantity: n(m.quantity), unit: m.unit, rate_snapshot: m.rate_snapshot, rate_source: m.rate_source, rate_snapshot_at: m.rate_snapshot_at }));
    const fp = activeProducts.find((p) => p.id === form.product_id);
    const payload = {
      ...form,
      product_name: fp ? fp.product_name : form.product_name,
      unit: form.unit || (fp && fp.unit) || "MT",
      production_quantity: n(form.production_quantity),
      output_quantity: n(form.production_quantity),
      labour_cost: n(form.labour_cost), electricity_cost: n(form.electricity_cost),
      machine_cost: n(form.machine_cost), other_cost: n(form.other_cost),
      raw_materials: clean(raw),
      packaging: clean(pack),
    };
    try {
      if (editing) await api.put(`/production/${editing.id}`, payload);
      else await api.post("/production", payload);
      toast.success(`Production ${editing ? "updated" : "recorded"}`);
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/production/${deleteId}`); toast.success("Deleted"); setDeleteId(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const filtered = useMemo(() => {
    if (!query) return items;
    const q = query.toLowerCase();
    return items.filter((it) => ["product_name", "batch_number", "production_no", "warehouse"].some((k) => String(it[k] || "").toLowerCase().includes(q)));
  }, [items, query]);

  const MatTableInline = null; // removed inline component (hoisted to module scope to fix input focus)
  return (
    <div data-testid="module-production">
      <div className="mb-5"><h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-head">Production</h1>
        <p className="text-sm text-slate-500 mt-0.5">Batch production · BOM-driven consumption, costing &amp; finished-goods stock</p></div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <p className="text-sm text-slate-500">{filtered.length} record{filtered.length !== 1 ? "s" : ""}</p>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="search-production" placeholder="Search..." value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9 w-full sm:w-52 bg-white" />
          </div>
          {canW && <Button data-testid="add-production-btn" onClick={openCreate} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5"><Plus className="w-4 h-4" /><span className="hidden sm:inline">Add New</span></Button>}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b border-slate-200 text-left">
            {["Date", "Batch", "Finished Product", "Output", "Total Cost", "Cost/Unit", "Status", ""].map((h, i) => (
              <th key={i} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 ${h === "" ? "text-right" : ""}`}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">Loading...</td></tr>
              : filtered.length === 0 ? <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">No production records</td></tr>
              : filtered.map((row) => (
                <tr key={row.id} data-testid={`row-production-${row.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 whitespace-nowrap text-slate-700">{fmtDate(row.production_date)}</td>
                  <td className="px-4 py-3 whitespace-nowrap font-medium text-slate-800">{row.batch_number}</td>
                  <td className="px-4 py-3 text-slate-700">{row.product_name}</td>
                  <td className="px-4 py-3 whitespace-nowrap font-mono tabular-nums text-slate-900">{qtyf(row.production_quantity || row.output_quantity)} {row.unit}</td>
                  <td className="px-4 py-3 whitespace-nowrap font-mono tabular-nums text-slate-900">{fmtINR(row.total_production_cost)}</td>
                  <td className="px-4 py-3 whitespace-nowrap font-mono tabular-nums text-slate-900">{fmtINR(row.cost_per_unit)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600">{row.status || "Completed"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="flex items-center justify-end gap-1">
                      <Button data-testid={`pdf-${row.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-[#0B132B]" title="Download Production Slip" onClick={() => window.open(`${API}/pdf/production/${row.id}?token=${localStorage.getItem("token")}`, "_blank")}><FileText className="w-4 h-4" /></Button>
                      {canW && <Button data-testid={`edit-${row.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-[#0B132B]" onClick={() => openEdit(row)}><Pencil className="w-4 h-4" /></Button>}
                      {canDel && <Button data-testid={`delete-${row.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-red-600" onClick={() => setDeleteId(row.id)}><Trash2 className="w-4 h-4" /></Button>}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><Factory className="w-5 h-5" /> {editing ? "Edit" : "New"} Production</DialogTitle><DialogDescription className="sr-only">Production batch form</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Production No</label>
              <Input data-testid="field-production_no" value={form.production_no || ""} onChange={(e) => setForm({ ...form, production_no: e.target.value })} className="bg-white" /></div>
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Batch Number *</label>
              <Input data-testid="field-batch_number" value={form.batch_number || ""} onChange={(e) => setForm({ ...form, batch_number: e.target.value })} className="bg-white" /></div>
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Production Date</label>
              <Input data-testid="field-production_date" type="date" value={form.production_date || ""} onChange={(e) => setForm({ ...form, production_date: e.target.value })} className="bg-white" /></div>
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Finished Product *</label>
              <Select value={form.product_id || ""} onValueChange={(v) => { const p = finishedOpts.find((x) => x.id === v); setForm({ ...form, product_id: v, product_name: p ? p.product_name : "", unit: p ? (p.unit || "MT") : form.unit }); }}>
                <SelectTrigger data-testid="field-finished-product" className="bg-white"><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent>{finishedOpts.map((p) => <SelectItem key={p.id} value={p.id}>{p.product_name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Output Quantity *</label>
              <Input data-testid="field-production_quantity" type="number" step="0.001" value={form.production_quantity || ""} onChange={(e) => setForm({ ...form, production_quantity: e.target.value })} className="bg-white" /></div>
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Unit</label>
              <Select value={form.unit || "MT"} onValueChange={(v) => setForm({ ...form, unit: v })}><SelectTrigger className="bg-white"><SelectValue /></SelectTrigger><SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Warehouse / Location</label>
              <Input data-testid="field-warehouse" value={form.warehouse || ""} onChange={(e) => setForm({ ...form, warehouse: e.target.value })} className="bg-white" /></div>
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</label>
              <Select value={form.status || "Completed"} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="field-status" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>{["Planned", "In Progress", "Completed", "Cancelled"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>

          <div className="flex justify-end mt-3">
            <Button data-testid="load-bom-btn" size="sm" variant="outline" className="rounded-sm gap-1.5" onClick={loadBom}><Wand2 className="w-3.5 h-3.5" /> Load Recipe (BOM)</Button>
          </div>

          <MatTable title="Raw Materials Consumed" rows={raw} setRows={setRaw} setter={setRawMat} opts={rawOpts} testid="raw" rateMap={rateMap} />
          <MatTable title="Packaging Materials Consumed" rows={pack} setRows={setPack} setter={setPackMat} opts={packOpts} testid="pack" rateMap={rateMap} />
          <p className="text-[11px] text-slate-500 mt-1">Rates are fetched automatically from the Inventory Actual Unit Cost (weighted average of real purchase bills incl. landed cost) and are not editable. The rate used is frozen on the saved record, so older production costs never change.</p>

          <div className="mt-4 border-t border-slate-200 pt-3">
            <h3 className="text-sm font-semibold text-slate-800 font-head mb-2">Production Expenses</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Labour</label>
                <Input data-testid="field-labour_cost" type="number" min="0" value={form.labour_cost ?? 0} onChange={(e) => setForm({ ...form, labour_cost: e.target.value })} className="bg-white" /></div>
              <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Electricity</label>
                <Input data-testid="field-electricity_cost" type="number" min="0" value={form.electricity_cost ?? 0} onChange={(e) => setForm({ ...form, electricity_cost: e.target.value })} className="bg-white" /></div>
              <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Machine / Diesel</label>
                <Input data-testid="field-machine_cost" type="number" min="0" value={form.machine_cost ?? 0} onChange={(e) => setForm({ ...form, machine_cost: e.target.value })} className="bg-white" /></div>
              <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Other</label>
                <Input data-testid="field-other_cost" type="number" min="0" value={form.other_cost ?? 0} onChange={(e) => setForm({ ...form, other_cost: e.target.value })} className="bg-white" /></div>
            </div>
          </div>

          <div className="mt-4 bg-slate-50 border border-slate-200 rounded-sm p-4" data-testid="cost-summary">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1.5 text-sm">
              <div className="flex justify-between text-slate-600"><span>Raw Material Cost</span><span className="font-mono tabular-nums">{fmtINR(costs.rawCost)}</span></div>
              <div className="flex justify-between text-slate-600"><span>Packaging Cost</span><span className="font-mono tabular-nums">{fmtINR(costs.pkgCost)}</span></div>
              <div className="flex justify-between text-slate-600"><span>Expenses</span><span className="font-mono tabular-nums">{fmtINR(n(form.labour_cost) + n(form.electricity_cost) + n(form.machine_cost) + n(form.other_cost))}</span></div>
              <div className="flex justify-between font-semibold text-slate-900 border-t border-slate-200 pt-1 mt-1" data-testid="total-production-cost"><span>Total Production Cost</span><span className="font-mono tabular-nums">{fmtINR(costs.total)}</span></div>
              <div className="flex justify-between font-semibold text-[#0B132B] border-t border-slate-200 pt-1 mt-1 sm:col-span-2" data-testid="cost-per-unit"><span>Cost per Unit ({qtyf(form.production_quantity)} {form.unit})</span><span className="font-mono tabular-nums">{fmtINR(costs.perUnit)}</span></div>
            </div>
          </div>

          <div className="space-y-1 mt-3"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Remarks</label>
            <Textarea data-testid="field-remarks" value={form.remarks || ""} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="bg-white" /></div>

          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="save-btn" onClick={save} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm">{editing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete production record?</AlertDialogTitle><AlertDialogDescription>This reverses the related stock movements (raw/packaging consumed and finished goods produced).</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel><AlertDialogAction data-testid="confirm-delete-btn" onClick={doDelete} className="bg-red-600 hover:bg-red-700 rounded-sm">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
